/**
 * OutOfTokensModal — global upgrade prompt that pops when any API call
 * returns 402 "Insufficient tokens".
 *
 * The provider installs a single axios response interceptor that intercepts
 * 402 responses, captures the detail message, and surfaces a modal with
 * three clear paths to recovery:
 *   - View pricing plans (highest LTV conversion)
 *   - Buy a token top-up (immediate revenue)
 *   - Dismiss + remind later
 *
 * Why a global modal vs per-page handling?
 *  - Every generation endpoint can throw 402 (content, video, photo-reel,
 *    bulk, variations). Handling it per-page = duplicated UX and inconsistent
 *    copy. A global handler also ensures the "upgrade prompt" is the FIRST
 *    thing users see when they hit the wall — not a generic toast they miss.
 */
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Zap, Coins, ArrowRight, Sparkles, X } from "lucide-react";

const OutOfTokensContext = createContext(null);

export const useOutOfTokens = () => {
  const ctx = useContext(OutOfTokensContext);
  if (!ctx) {
    throw new Error("useOutOfTokens must be used within OutOfTokensProvider");
  }
  return ctx;
};

export const OutOfTokensProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState("");
  const navigate = useNavigate();
  // Track last-shown timestamp to avoid hammering the user with repeats
  // when several requests fail in quick succession on the same page.
  const lastShownRef = useRef(0);

  const showOutOfTokens = (msg) => {
    const now = Date.now();
    if (now - lastShownRef.current < 1500) return;  // 1.5s debounce
    lastShownRef.current = now;
    setDetail(msg || "You're out of tokens. Top up or upgrade to keep creating.");
    setOpen(true);
  };

  // Install one global axios response interceptor that watches for 402s.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (r) => r,
      (error) => {
        try {
          const status = error?.response?.status;
          const data = error?.response?.data;
          const msg = typeof data?.detail === "string" ? data.detail : "";
          // 402 = Payment Required. We also match by substring so the
          // friendlier copy from /content endpoints still triggers it.
          const isTokenBlock =
            status === 402 ||
            /insufficient tokens|out of tokens|need .* tokens/i.test(msg);
          if (isTokenBlock) {
            showOutOfTokens(msg);
          }
        } catch (e) {
          // never let the interceptor itself throw
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <OutOfTokensContext.Provider value={{ showOutOfTokens }}>
      {children}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-white border-ink-200 max-w-md"
          data-testid="out-of-tokens-modal"
        >
          <DialogHeader>
            <div className="flex items-start justify-between">
              <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-coral-500 text-white">
                <Zap className="w-6 h-6" />
              </span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-ink-400 hover:text-ink-950 transition-colors"
                aria-label="Close"
                data-testid="out-of-tokens-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <DialogTitle className="text-2xl font-display font-black text-ink-950 tracking-tight pt-3">
              You&apos;re out of tokens
            </DialogTitle>
            <DialogDescription className="sr-only">
              You&apos;ve run out of tokens. Upgrade your plan or buy a top-up to keep generating creatives.
            </DialogDescription>
          </DialogHeader>

          <p className="text-sm text-ink-700 leading-relaxed -mt-2">
            {detail}
          </p>

          <div className="grid gap-2 pt-2">
            <Button
              onClick={() => {
                setOpen(false);
                navigate("/dashboard/subscription");
              }}
              data-testid="out-of-tokens-upgrade-btn"
              className="w-full h-12 bg-ink-950 hover:bg-coral-500 text-white text-sm font-bold uppercase tracking-[0.18em] gap-2 transition-colors"
            >
              <Sparkles className="w-4 h-4" />
              See plans
              <ArrowRight className="w-4 h-4 ml-auto" />
            </Button>
            <Button
              onClick={() => {
                setOpen(false);
                navigate("/dashboard/tokens");
              }}
              variant="outline"
              data-testid="out-of-tokens-topup-btn"
              className="w-full h-12 border-ink-200 hover:bg-ink-100 text-ink-950 text-sm font-bold uppercase tracking-[0.18em] gap-2"
            >
              <Coins className="w-4 h-4 text-amber-500" />
              Buy token top-up
              <ArrowRight className="w-4 h-4 ml-auto" />
            </Button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="out-of-tokens-dismiss"
              className="w-full mt-1 text-xs font-medium text-ink-500 hover:text-ink-950 py-2 transition-colors"
            >
              Remind me later
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </OutOfTokensContext.Provider>
  );
};
